// utils/referral.js
//
// Supplier referral capture — TMA-only. A supplier's shareable link opens the
// Mini App with `start_param = "ref_<opaqueCode>"`. We capture it verbatim at
// boot (see App.vue) and attach it as `referral_code` on the existing
// POST /registrations payload (see MembershipRequestForm.vue) so the backend
// can attribute the new client to the supplier and gift PoolShare on approval.
//
// Pure module — no Vue/F7 imports, no BE truth. All validation/eligibility of
// the code itself lives backend-side; this file only parses, stores, and
// expires the token client-side.
//
// See _bmad-output/implementation-artifacts/spec-supplier-referral-poolshare-gift.md

/** localStorage key holding `{ code, ts }`. */
export const REFERRAL_STORAGE_KEY = 'poolit:referral';

/** 30-day TTL: a code older than this is considered stale and dropped so it
 * can't attach to an unrelated, much-later registration. */
export const REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Hard ceiling on a persisted token's length. 512 is Telegram's own `startapp`
 * limit AND the backend's `referral_code` bound (`max:512`, see the 2026-07-19
 * BE→FE invite-only brief). A token longer than this can only be hand-crafted —
 * no real deep link produces it — and the backend now rejects it as
 * `REFERRAL_INVALID`, so we cap on write rather than persist an oversize value
 * that just re-sends a token that can never resolve for the full 30-day TTL.
 */
export const REFERRAL_CODE_MAX_LENGTH = 512;

/**
 * Extract a referral code from a raw TMA `start_param`.
 *
 * The `ref_`/`ref-` prefix (case-insensitive) disambiguates referral tokens
 * from control verbs already living in the same `start_param` slot (e.g.
 * `launch` — see spec-telegram-launch-flow.md). The token is returned
 * verbatim; the backend owns further parsing/validation.
 *
 * @param {unknown} startParam - Raw `tgWebAppStartParam` value (may be
 *   undefined, empty, or any other verb).
 * @returns {string|null} The verbatim token, or null when absent/not a
 *   referral/the reserved `launch` verb.
 */
export function extractReferralCode(startParam) {
  if (typeof startParam !== 'string') return null;
  const trimmed = startParam.trim();
  if (!trimmed) return null;
  if (trimmed.toLowerCase() === 'launch') return null;
  // Require at least one character after the `ref_`/`ref-` prefix, so a
  // prefix-only param (`ref_`) is never manufactured into an empty-code token.
  if (!/^ref[_-].+/i.test(trimmed)) return null;
  return trimmed;
}

/**
 * Persist a referral code with a fresh timestamp. Last-write-wins — a second
 * link opened later simply overwrites the first (spec: "Two links opened").
 * Stored verbatim, but truncated to {@link REFERRAL_CODE_MAX_LENGTH}.
 *
 * @param {string} code - Verbatim referral token.
 */
export function saveReferralCode(code) {
  try {
    // Cap on write: the single choke point for every persisted token, so an
    // over-length value can never be stored (and later re-sent) verbatim.
    // Real deep-link tokens are far under the ceiling, so this is inert for them.
    localStorage.setItem(
      REFERRAL_STORAGE_KEY,
      JSON.stringify({ code: code.slice(0, REFERRAL_CODE_MAX_LENGTH), ts: Date.now() }),
    );
  } catch {
    // Private-mode / quota-exceeded / storage-disabled — referral capture is
    // strictly additive, never allowed to throw into the boot path.
  }
}

/**
 * Read the extracted code (if any) out of a raw TMA `start_param` and store
 * it. No-op for `launch`/empty/unrecognized values.
 *
 * @param {unknown} startParam - Raw `tgWebAppStartParam` value.
 * @returns {string|null} The captured code, or null if nothing was captured.
 */
export function captureReferralFromStartParam(startParam) {
  const code = extractReferralCode(startParam);
  if (code) saveReferralCode(code);
  return code;
}

/**
 * Read the stored referral code, enforcing the 30-day TTL.
 *
 * Self-healing: a corrupt (unparseable / wrong-shape) or expired entry is
 * cleared as a side effect so it can't linger and be re-rejected on every boot.
 *
 * @returns {string|null} The stored code, or null if absent, corrupt, or
 *   expired.
 */
export function getReferralCode() {
  let raw;
  try {
    raw = localStorage.getItem(REFERRAL_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearReferralCode();
    return null;
  }
  if (!parsed || typeof parsed.code !== 'string' || typeof parsed.ts !== 'number') {
    clearReferralCode();
    return null;
  }

  // A negative age means the device clock moved backward since capture; treat
  // that as suspect and expire the code (fail-safe: at worst, no attribution).
  const age = Date.now() - parsed.ts;
  if (age < 0 || age > REFERRAL_TTL_MS) {
    clearReferralCode();
    return null;
  }

  return parsed.code;
}

/**
 * Remove the stored referral code. Called once, on a confirmed successful
 * registration submit — a failed submit must retain it so a retry keeps
 * attribution (spec: "Failed submit").
 */
export function clearReferralCode() {
  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to clean up.
  }
}
